import analytics from '../utils/analytics';
import { useEffect } from 'react';
import {
    resolveAnalyticsUrl,
    extractContentfulEntryId
} from '../utils/content-analytics-resources';
import type { ContentLinkAnalytics } from '../components/atlas-link/track';

/**
 * Parameters for tracking content link viewed analytics
 *
 * Uses ContentLinkAnalytics as the base type, with required fields for viewed events.
 */
export type ContentLinkViewedAnalyticsParams = Required<
    Pick<
        ContentLinkAnalytics,
        | 'linkType'
        | 'context'
        | 'position'
        | 'linkContentAri'
        | 'linkContentResolvedUrl'
    >
> &
    Pick<ContentLinkAnalytics, 'parentPageType'> & {
        /** Whether to actually track the analytics event (optional, defaults to true) */
        shouldTrack?: boolean;
    };

/**
 * Hook to track content link viewed analytics events
 *
 * This hook automatically tracks when content links are viewed and can be used
 * across different contexts (search results, sidebar, browse pages, etc.)
 *
 * @param params - The analytics parameters for tracking content link views
 *
 * @example
 * ```tsx
 * // For search results
 * useContentLinkViewedAnalytics({
 *   linkType: 'card',
 *   context: 'search-results',
 *   position: 1,
 *   linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *   linkContentResolvedUrl: 'https://example.com/doc/123'
 * });
 *
 * // For sidebar content with parent page type
 * useContentLinkViewedAnalytics({
 *   linkType: 'text-link',
 *   context: 'sidebar',
 *   position: 2,
 *   linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *   linkContentResolvedUrl: 'https://example.com/doc/789',
 *   parentPageType: 'content-hub'
 * });
 *
 * // For browse content with parent page type
 * useContentLinkViewedAnalytics({
 *   linkType: 'card',
 *   context: 'browse',
 *   position: 3,
 *   linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry456',
 *   linkContentResolvedUrl: 'https://example.com/doc/456',
 *   parentPageType: 'topic-set'
 * });
 *
 * // For conditional tracking (e.g., don't track current page)
 * useContentLinkViewedAnalytics({
 *   linkType: 'text-link',
 *   context: 'sidebar',
 *   position: 1,
 *   linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *   linkContentResolvedUrl: 'https://example.com/doc/789',
 *   shouldTrack: !isCurrentPage
 * });
 * ```
 */
export const useContentLinkViewedAnalytics = (
    params: ContentLinkViewedAnalyticsParams
): void => {
    const {
        linkType,
        context,
        position,
        linkContentAri,
        linkContentResolvedUrl,
        parentPageType,
        shouldTrack = true
    } = params;

    useEffect(() => {
        // Only track analytics if shouldTrack is true
        if (!shouldTrack) {
            return;
        }

        // Extract Contentful entry ID from content ARI
        const linkContentEntryId = extractContentfulEntryId(linkContentAri);

        // Resolve the URL properly on client-side
        const resolvedUrl = resolveAnalyticsUrl(linkContentResolvedUrl);

        // Build the base analytics properties using UI event structure
        const analyticsProperties = {
            event: 'viewed',
            eventComponent: 'content-link',
            eventContainer: context,
            // Required properties for content link tracking
            position,
            link_type: linkType,
            link_content_ari: linkContentAri,
            link_content_entry_id: linkContentEntryId,
            link_content_resolved_url: resolvedUrl,
            // Include parent page type if provided
            ...(parentPageType && { parent_page_type: parentPageType })
        };

        analytics.trackEvent('content-link viewed', analyticsProperties);
    }, [
        linkType,
        context,
        position,
        linkContentAri,
        linkContentResolvedUrl,
        parentPageType,
        shouldTrack
    ]);
};
